import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import QueryInput from './QueryInput';
import Card from '../Card';
import { useAuth } from '../../contexts/AuthContext';
import QueryResultCard from './QueryResultCard';
import SaveQueryModal from './SaveQueryModal';
import AddToDashboardModal from './AddToDashboardModal';
import QueryError from './QueryError';
import { useConfig } from '../../contexts/ConfigContext';
import useFollowUpQuery from '../../hooks/useFollowUpQuery';
import { getFirestore, doc, onSnapshot, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useQueryActions } from '../../hooks/useQueryActions';



class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleReset = () => {
    // Clear any problematic state
    localStorage.removeItem('recentSearches');
    sessionStorage.clear();
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Card className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
          <h1 className="font-bold">Something went wrong</h1>
          <p className="mt-2">We encountered an error while processing your request.</p>
          <button
            onClick={this.handleReset}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            Reset Application
          </button>
        </Card>
      );
    }

    return this.props.children;
  }
}

const QueryComponent = forwardRef(
  (
    {
      mainContentRef,
      onQueryResult,
      onQueryStart,
      queryResult,
      isLoading,
      onSaveQuery,
      onAddToDashboard,
      onFollowUpQuery,
      onDashboardUpdate,
      dashboardManager,
      isQueryJustAdded,
      setIsQueryJustAdded,
      isAskPage,
      isDashboardMode = false,
      onClearResult,
      onAddToLibrary,
      saveQuerySettings,
      getQuerySettings,
      getFilteredQueries,
      hideProposedQuestions
    },
    ref
  ) => {

    const componentRef = useRef(null);
    const [error, setError] = useState(null);
    const [isSaveQueryModalOpen, setIsSaveQueryModalOpen] = useState(false);
    const [isAddToDashboardModalOpen, setIsAddToDashboardModalOpen] = useState(false);
    const [queryName, setQueryName] = useState('');
    const [isShared, setIsShared] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [savedQueryId, setSavedQueryId] = useState(null);
    const [isExplanationLoading, setIsExplanationLoading] = useState(false);
    const [isExplanationPending, setIsExplanationPending] = useState(false);
    const [explanation, setExplanation] = useState('');
    const [showResultCard, setShowResultCard] = useState(false);
    const [truncationMessage, setTruncationMessage] = useState('');
    const [isFollowUp, setIsFollowUp] = useState(false);
    const [dashboards, setDashboards] = useState([]);
    const [recentSearches, setRecentSearches] = useState([]);
    const [currentQuery, setCurrentQuery] = useState('');
    const explanationListenerRef = useRef(null);
    const explanationTimeoutRef = useRef(null);
    const { config, isLoading: isConfigLoading } = useConfig();

    useEffect(() => {
      // Cleanup function for explanation listeners
      return () => {
        if (explanationListenerRef.current) {
          console.log('Cleaning up explanation listener on unmount');
          explanationListenerRef.current();
          explanationListenerRef.current = null;
        }
        if (explanationTimeoutRef.current) {
          clearTimeout(explanationTimeoutRef.current);
          explanationTimeoutRef.current = null;
        }
      };
    }, []);

    const {
      selectedText,
      selectedColumns,
      handleTextSelection,
      handleFollowUpSubmit: hookHandleFollowUpSubmit,
      resetFollowUpState
    } = useFollowUpQuery(async (submittedQuery, selectionData) => {
      await handleFollowUpQuery(submittedQuery, selectionData.text, queryResult?.sqlQuery, selectionData.columns);
    });
    const resultRef = React.useRef(null);
    const { currentUser, isAdmin, isAIManager } = useAuth();
    const actionPanelRef = useRef(null);

    useImperativeHandle(ref, () => ({
      scrollIntoView: () => {
        if (componentRef.current) {
          componentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
    }));

    useEffect(() => {
      fetchDashboards();
    }, [currentUser?.email, config?.core?.projectId]); // Add config dependency

    const fetchDashboards = async () => {
      // Don't fetch if config or user is not ready
      if (!currentUser?.email || !config?.core?.projectId) {
        return;
      }
      
      try {
        const response = await fetch(
          'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/handleDashboardOperation',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              data: {
                operation: 'get',
                userId: currentUser.email,
                isAdmin: isAdmin,
              },
            }),
          }
        );
        const data = await response.json();
        setDashboards(data);
      } catch (error) {
        // Only set error if both user and config exist (not an initialization issue)
        if (currentUser?.email && config?.core?.projectId) {
          setError('Failed to fetch dashboards. Please try again later.');
        }
      }
    };

      const generateExplanation = useCallback(async (sqlQuery, documentId, savedQueryId, userQuery, justFetch = false) => {
        setIsExplanationLoading(true);
        setIsExplanationPending(false);
        try {
          const requestData = { sqlQuery, documentId, savedQueryId, userQuery, justFetch };
      
          if (!requestData.documentId && !requestData.savedQueryId) {
            throw new Error('Missing necessary identifiers.');
          }
      
          const response = await fetch(
            'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/generateExplanation',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ data: requestData }),
            }
          );
          if (!response.ok) {
            throw new Error('Failed to generate explanation.');
          }
          const data = await response.json();
          return data.explanation;
        } catch (error) {
          throw error;
        } finally {
          setIsExplanationLoading(false);
        }
      }, [config]);
  
      const fetchRecentSearches = useCallback(async () => {
        const MAX_RETRIES = 3;
        const INITIAL_DELAY = 1000;
      
        const fetchWithRetry = async (retryCount) => {
          try {
            const functions = getFunctions();
            const getRecentSearchesCallable = httpsCallable(functions, 'getRecentSearches');
            
            const result = await getRecentSearchesCallable();
            const data = result.data;
            
            // Validate and sanitize the data
            const validSearches = Array.isArray(data.recentSearches) 
              ? data.recentSearches.filter(search => 
                  search && 
                  typeof search.userQuery === 'string' &&
                  search.documentId
                )
              : [];
            
            localStorage.setItem('recentSearches', JSON.stringify(validSearches));
            setRecentSearches(validSearches);
            return validSearches;
      
          } catch (error) {
            console.error('Error in fetchRecentSearches:', error);
            
            if (retryCount < MAX_RETRIES - 1) {
              const delay = INITIAL_DELAY * Math.pow(2, retryCount);
              await new Promise(resolve => setTimeout(resolve, delay));
              return fetchWithRetry(retryCount + 1);
            } else {
              try {
                const storedSearches = localStorage.getItem('recentSearches');
                return storedSearches ? JSON.parse(storedSearches) : [];
              } catch (e) {
                return [];
              }
            }
          }
        };
      
        try {
          return await fetchWithRetry(0);
        } catch (error) {
          return [];
        }
      }, [currentUser.email]);

      // Initialize useQueryActions hook BEFORE handleSubmit
      const { executeActions } = useQueryActions({
        actionPanelRef,
        onQueryResult,
        onSaveQuery: () => {}, // Temporary placeholder
        onAddToLibrary,
        onScheduleQuery: () => {}, // Placeholder for future implementation
        onShareUrl: () => {} // Placeholder for future implementation
      });

      useEffect(() => {
        fetchRecentSearches();
      }, [fetchRecentSearches]);
  
// NEW CODE
const handleSubmit = useCallback(
  async (
    submittedQuery,
    isFollowUp = false,
    highlightedText = '',
    originalSqlQuery = '',
    isRecentSearch = false,
    recentSearchDocumentId = null,
    selectedColumns = [],
    focusType = 'none',
    context = {}
  ) => {
    
    // For follow-up queries, store the enhanced version that includes context
    if (isFollowUp && highlightedText && selectedColumns && selectedColumns.length > 0) {
      const columnsStr = selectedColumns.length > 0 
        ? `[columns: ${selectedColumns.join(', ')}]` 
        : '';
      const enhancedDisplayQuery = `${submittedQuery} (regarding "${highlightedText}" ${columnsStr})`;
      setCurrentQuery(enhancedDisplayQuery);
    } else {
      // For regular queries, use context.displayQuery or submittedQuery
      setCurrentQuery(context.displayQuery || submittedQuery);
    }

    // Determine the actual query to send to the backend
    // If context contains originalQuery (from QuickFilter), use that instead
    const actualQueryForBackend = (context?.isQuickFilter && context?.originalQuery) 
      ? context.originalQuery 
      : submittedQuery;

    
    onQueryStart();
    setError(null);
    setShowResultCard(false);
    setTruncationMessage('');
    setIsQueryJustAdded(false);
    setIsExplanationPending(false);
    let responseData = null;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      const userId = currentUser?.email || 'anonymous';

      let endpoint = 'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/processDynamicQueryWithCORS';
      let enhancedQuery = actualQueryForBackend;
      if (isFollowUp && highlightedText && selectedColumns && selectedColumns.length > 0) {
        const columnsStr = selectedColumns.length > 0 
          ? `[columns: ${selectedColumns.join(', ')}]` 
          : '';
        enhancedQuery = `${actualQueryForBackend} (regarding "${highlightedText}" ${columnsStr})`;
      }

      let body = {
        data: {
          userQuery: enhancedQuery, // Use the enhanced query that includes highlighted context
          userId: userId,
          isFollowUp: isFollowUp,
          highlightedText: highlightedText, // Still send the highlightedText separately
          originalDocumentId: isFollowUp ? queryResult?.documentId : context.tangoDocumentId || null,
          documentId: isFollowUp ? queryResult?.documentId : context.tangoDocumentId || null,
          originalSavedQueryId: isFollowUp ? queryResult?.savedQueryId : null,
          originalSqlQuery: isFollowUp ? originalSqlQuery : null,
          selectedColumns: queryResult?.isTransposed ? 
            selectedColumns.map(col => col.replace(/^Value_\d+$/, 'Field')) : 
            selectedColumns,
          focusType: focusType,
          isIndexed: context?.isFromValueSearchList || false,
          isTransposed: queryResult?.isTransposed || false,
          columnConfig: context.columnConfig,
          valueContext: context.valueContext || null,
          displayQuery: context.displayQuery || submittedQuery, // Store the display query for reference
          isQuickFilter: context?.isQuickFilter || false,
          isTangoId: focusType === 'tangoId',
          tangoDocumentId: context.tangoDocumentId || null,
          originalQuestion: context.originalQuestion || null,
          usesTableMention: context.usesTableMention || false // Pass table mention flag for intent detection
        },
        context: { auth: true },
      };
      

      if (isRecentSearch) {
        endpoint = 'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/runRecentSearch';
        body = {
          data: {
            documentId: recentSearchDocumentId,
            userId: userId,
          },
        };
      }
            
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
              signal: controller.signal,
            });
            
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const responseData = await response.json();
            clearTimeout(timeoutId);

            // Handle truncation message consistently for both new and recent searches
            if (responseData.truncationMessage) {
              setTruncationMessage(responseData.truncationMessage);
            } else if (responseData.result && responseData.result.length > 2000) {
              setTruncationMessage('The query result has been limited to 2,000 rows. Metrics are calculated on displayed rows only.');
            }

            if (!response.ok || responseData.error) {
              throw responseData;
            }

            // Handle action-only requests
            if (responseData.actionOnly && responseData.detectedActions && responseData.detectedActions.length > 0) {
              console.log('Action-only request detected with results');
              
              // For action-only requests, we still process the results normally
              // The actions will be applied through the normal flow
              // Don't return early - continue processing
            }

            // Execute actions if detected (for regular queries with results)
            let cardSettingsFromActions = {};
            if (responseData.detectedActions && responseData.detectedActions.length > 0) {
              console.log('Detected actions:', responseData.detectedActions);
              try {
                const actionResults = await executeActions(responseData.detectedActions, responseData);
                
                // If actions provided card settings modifications, merge them
                if (actionResults.cardSettings) {
                  cardSettingsFromActions = actionResults.cardSettings;
                  console.log('Actions provided card settings:', cardSettingsFromActions);
                }
              } catch (actionError) {
                console.error('Error executing detected actions:', actionError);
                // Continue without action modifications if execution fails
              }
            }

            // Apply Beyond Analytics visualization settings
            let cardSettingsFromBeyond = {};
            if (responseData.beyondAnalytics && responseData.beyondAnalytics.analyticsRequired && 
                responseData.beyondAnalytics.functions && responseData.beyondAnalytics.functions.length > 0) {
              console.log('Beyond Analytics detected:', responseData.beyondAnalytics);
              
              // Extract visualization type from the first function
              const firstFunction = responseData.beyondAnalytics.functions[0];
              if (firstFunction.visualization) {
                cardSettingsFromBeyond = {
                  viewMode: firstFunction.visualization,
                  chartType: firstFunction.visualization
                };
                console.log('Beyond Analytics visualization settings:', cardSettingsFromBeyond);
              }
            }
      
            const truncationMessage = responseData.totalCount > 2000
              ? `The query result has been limited to 2,000 rows. Metrics are calculated on displayed rows only.`
              : '';
      
            setTruncationMessage(truncationMessage);
      
            // Handle explanation with Firestore listener
            if (!responseData.explanation || responseData.explanationInProgress || isFollowUp) {
              setIsExplanationPending(true);

              // Set up a real-time listener for the explanation
              const documentId = responseData.documentId;
              if (documentId) {
                
                // Clean up any existing listener first
                if (explanationListenerRef.current) {
                  console.log('Cleaning up previous explanation listener');
                  explanationListenerRef.current();
                  explanationListenerRef.current = null;
                }
                if (explanationTimeoutRef.current) {
                  clearTimeout(explanationTimeoutRef.current);
                  explanationTimeoutRef.current = null;
                }
                
                // Define a timeout to prevent listening indefinitely
                explanationTimeoutRef.current = setTimeout(() => {
                  if (explanationListenerRef.current) {
                    console.log('Explanation timeout - cleaning up listener');
                    explanationListenerRef.current();
                    explanationListenerRef.current = null;
                    setIsExplanationPending(false);
                    
                    // Set a placeholder explanation if none was received
                    onQueryResult((prevResult) => ({
                      ...prevResult,
                      explanation: {
                        explanation: "Timed out waiting for explanation.",
                        title: "",
                        tables: [],
                        filters: []
                      }
                    }));
                  }
                }, 60000); // 60 second timeout (1 minute)
                
                try {
                  const db = getFirestore();
                  const docRef = doc(db, 'LLM Queries', documentId);
                  
                  explanationListenerRef.current = onSnapshot(docRef, (docSnapshot) => {
                    if (docSnapshot.exists()) {
                      const data = docSnapshot.data();
                      
                      if (data.explanation) {
                        console.log('Explanation received - cleaning up listener');
                        
                        // Clear the timeout since we got our explanation
                        if (explanationTimeoutRef.current) {
                          clearTimeout(explanationTimeoutRef.current);
                          explanationTimeoutRef.current = null;
                        }
                        
                        // Instead of just assigning the explanation, ensure it's a proper object
                        let parsedExplanation = data.explanation;
                        
                        // If it's a string, try to parse it as JSON, otherwise use as is
                        if (typeof data.explanation === 'string') {
                          try {
                            parsedExplanation = JSON.parse(data.explanation);
                          } catch (error) {
                            // If it fails to parse, just use it as a string in an object
                            parsedExplanation = { explanation: data.explanation };
                          }
                        }
                        
                        // Update the query result with the properly parsed explanation
                        onQueryResult((prevResult) => ({
                          ...prevResult,
                          explanation: parsedExplanation
                        }));

                        // Stop pending state and detach listener
                        setIsExplanationPending(false);
                        if (explanationListenerRef.current) {
                          explanationListenerRef.current();
                          explanationListenerRef.current = null;
                        }
                      }
                    }
                  }, (error) => {
                    console.error('Explanation listener error:', error);
                    if (explanationTimeoutRef.current) {
                      clearTimeout(explanationTimeoutRef.current);
                      explanationTimeoutRef.current = null;
                    }
                    setIsExplanationPending(false);
                    if (explanationListenerRef.current) {
                      explanationListenerRef.current();
                      explanationListenerRef.current = null;
                    }
                  });
                } catch (error) {
                  console.error('Failed to set up explanation listener:', error);
                  if (explanationTimeoutRef.current) {
                    clearTimeout(explanationTimeoutRef.current);
                    explanationTimeoutRef.current = null;
                  }
                  setIsExplanationPending(false);
                }
              } else {
                setIsExplanationPending(false);
              }
            }

                        // Get all available columns from the new result
                        const availableColumns = responseData.result && responseData.result.length > 0 
                        ? Object.keys(responseData.result[0]) 
                        : [];

                        let finalVisibleColumns = availableColumns;
                        let finalColumnOrder = availableColumns;

                        if (isFollowUp && context.columnConfig) {
                        }

                        // Determine card settings - prioritize existing settings from the response
                        const baseCardSettings = responseData.cardSettings || {};

                        // Create the card settings object, merging with context if needed
                        const mergedCardSettings = {
                          ...baseCardSettings,
                          ...cardSettingsFromBeyond,  // Apply Beyond Analytics settings
                          visibleColumns: (() => {
                        // If we have settings from the response already, use those first
                        if (baseCardSettings.visibleColumns && baseCardSettings.visibleColumns.length > 0) {
                          return baseCardSettings.visibleColumns;
                        }

                        const availableColumns = responseData.result && responseData.result.length > 0 
                          ? Object.keys(responseData.result[0]) 
                          : [];

                        if (isFollowUp && context.columnConfig) {
                          // First identify columns that should be hidden - these are columns that exist
                          // in previous query but weren't visible
                          const previouslyHiddenColumns = availableColumns.filter(col => 
                            context.columnConfig.columnOrder.includes(col) && 
                            !context.columnConfig.visibleColumns.includes(col)
                          );

                          // All columns should be visible except those explicitly hidden before
                          return availableColumns.filter(col => !previouslyHiddenColumns.includes(col));
                        }
                        return availableColumns;
                        })(),
                        columnOrder: (() => {
                        // If we have settings from the response already, use those first
                        if (baseCardSettings.columnOrder && baseCardSettings.columnOrder.length > 0) {
                          return baseCardSettings.columnOrder;
                        }

                        const availableColumns = responseData.result && responseData.result.length > 0 
                          ? Object.keys(responseData.result[0]) 
                          : [];

                        if (isFollowUp && context.columnConfig?.columnOrder) {
                          // Get new columns 
                          const newColumns = availableColumns.filter(col => 
                            !context.columnConfig.columnOrder.includes(col)
                          );
                          
                          // Get existing columns in their saved order
                          const existingColumns = context.columnConfig.columnOrder.filter(col => 
                            availableColumns.includes(col)
                          );

                          // Existing columns in saved order, then new columns at the end
                          return [...existingColumns, ...newColumns];
                        }
                        return availableColumns;
                        })(),
                        showSummary: baseCardSettings.showSummary ?? context.columnConfig?.showSummary ?? true,
                        viewMode: baseCardSettings.viewMode || context.columnConfig?.viewMode || 'table',
                        chartType: baseCardSettings.chartType || context.columnConfig?.chartType || 'bar',
                        syncScales: baseCardSettings.syncScales ?? context.columnConfig?.syncScales ?? false,
                        selectedXAxis: baseCardSettings.selectedXAxis || context.columnConfig?.selectedXAxis || '',
                        selectedYAxis: baseCardSettings.selectedYAxis || context.columnConfig?.selectedYAxis || [],
                        sortColumn: baseCardSettings.sortColumn || context.columnConfig?.sortColumn || '',
                        sortOrder: baseCardSettings.sortOrder || context.columnConfig?.sortOrder || 'asc',
                        activeFilters: baseCardSettings.activeFilters || context.columnConfig?.activeFilters || {},
                        transformationType: baseCardSettings.transformationType || null,
                        pivotConfig: baseCardSettings.pivotConfig || null
                        };

                        const newResult = {
                          ...responseData,
                          userQuery: submittedQuery,
                        isFollowUp: isFollowUp,
                        isSavedQuery: responseData.isSavedQuery || false,
                        name: responseData.name || '',
                        config: {
                        usedModel: responseData.usedModel || 'Unknown',
                        fallbackUsed: responseData.fallbackUsed || false,
                        },
                        retryCount: responseData.retryCount || 0,
                        retryResult: responseData.retryResult || 'N/A',
                        bigQueryError: responseData.bigQueryError || null,
                        result: responseData.result,
                        truncationMessage: truncationMessage,
                        explanation: responseData.explanation || 'Generating explanation...',
                        documentId: responseData.documentId || (isFollowUp ? queryResult?.documentId : null),
                        sqlQuery: responseData.sqlQuery || queryResult?.sqlQuery,
                        savedQueryId: isFollowUp
                        ? (responseData.savedQueryId || queryResult?.savedQueryId || null)
                        : (responseData.savedQueryId ?? null),
                        truncated: responseData.totalCount > 2000,
                        totalCount: responseData.totalCount,
                        schema: responseData.schema || {
                        fields: responseData.result && responseData.result.length > 0
                          ? Object.keys(responseData.result[0]).map((key) => ({
                              name: key,
                              type: typeof responseData.result[0][key] === 'number' ? 'FLOAT' : 'STRING',
                            }))
                          : [],
                        },
                        emptyResultSet: responseData.result && responseData.result.length === 0,
                        status: responseData.status || 'completed',
                        endTime: responseData.endTime || new Date().toISOString(),
                        isTransposed: responseData.isTransposed || false,
                        transposedResponse: responseData.transposedResponse || null,
                        transposedSchema: responseData.transposedSchema || null,
                        cardSettings: {
                          ...mergedCardSettings,
                          ...cardSettingsFromBeyond,  // Apply Beyond Analytics settings
                          ...cardSettingsFromActions   // Apply action-driven settings on top (highest priority)
                        }
                        };
      
            onQueryResult(newResult);
            setShowResultCard(true);
            setIsFollowUp(false);
      
            setTimeout(() => {
              if (mainContentRef && mainContentRef.current) {
                mainContentRef.current.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                });
              }
            }, 100);
      
            // Refresh recent searches after a successful query
            fetchRecentSearches();
          } catch (err) {
            let errorObject = {
              error: err.error || err.message || "An unexpected error occurred. Please try again.",
              details: err.details || {},
              documentId: err.documentId || null,
              userId: err.userId || currentUser?.email || 'anonymous',
              userQuestion: err.userQuestion || submittedQuery,
              retryCount: err.retryCount || 0,
              retryResult: err.retryResult || 'Failed',
              bigQueryError: err.bigQueryError || null,
              status: err.status || 'Error',
              endTime: err.endTime || new Date().toISOString(),
              queryVersions: err.queryVersions || []
            };
            
            setError(errorObject);
            onQueryResult(null);
          }
        },
        [onQueryResult, onQueryStart, currentUser, queryResult, mainContentRef, generateExplanation, executeActions]
      );
      
  
      // handleFollowUpQuery method
      const handleFollowUpQuery = useCallback(
        async (followUpQuery, highlightedText, originalSqlQuery, selectedColumns, currentColumnConfig) => {
          // Set initial query (will be updated later with enhanced version if applicable)
          setCurrentQuery(followUpQuery);
      
          // Detect if this is a removal request
          const isFilterRemoval = typeof followUpQuery === 'string' && 
                        followUpQuery.startsWith('recalculate without filter:');
          const isTableRemoval = typeof followUpQuery === 'string' && 
                                  followUpQuery.startsWith('recalculate without table:');
                                  
          // Create enhanced query for display if it's not a removal operation
          if (!isFilterRemoval && !isTableRemoval && highlightedText) {
            const columnsStr = selectedColumns && selectedColumns.length > 0 
              ? `[columns: ${selectedColumns.join(', ')}]` 
              : '';
            const enhancedDisplayQuery = `${followUpQuery} (regarding "${highlightedText}" ${columnsStr})`;
            setCurrentQuery(enhancedDisplayQuery);
          }

          // If it's a removal operation, show a loading indicator or feedback
          if (isFilterRemoval || isTableRemoval) {
            // Optional: You can set a state variable here to show a specific loading message
          }

          await handleSubmit(
            followUpQuery,
            true,
            isFilterRemoval || isTableRemoval ? '' : highlightedText,
            originalSqlQuery,
            false,
            null,
            isFilterRemoval || isTableRemoval ? [] : selectedColumns,
            isFilterRemoval ? 'filterRemoval' : isTableRemoval ? 'tableRemoval' : 'none',
            {
              columnConfig: currentColumnConfig,
              isTransposed: queryResult?.isTransposed || false,
              valueContext: currentColumnConfig?.valueContext || queryResult?.valueContext,
              activeFilters: currentColumnConfig?.activeFilters || {},
              isFilterRemoval,
              isTableRemoval
            }
          );
          
          setTimeout(() => {
            if (componentRef.current) {
              componentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        },
        [handleSubmit, componentRef, queryResult]
      );
    
      const handleClose = useCallback(() => {
        if (onClearResult) {
          onClearResult(); // This will clear the context
        }
        setShowResultCard(false); // Also hide the card locally
      }, [onClearResult]);


const handleQueryInputSubmit = useCallback(
  async (queryOrResult, isFollowUpMode = false, selectedText = '', context = {}) => {
    
    // Get current column configuration from the queryResult's cardSettings
    let columnConfig = {};
    if (isFollowUpMode && queryResult && queryResult.cardSettings) {
      columnConfig = {
        visibleColumns: queryResult.cardSettings.visibleColumns || [],
        columnOrder: queryResult.cardSettings.columnOrder || [],
        showSummary: queryResult.cardSettings.showSummary ?? true,
        viewMode: queryResult.cardSettings.viewMode,
        chartType: queryResult.cardSettings.chartType,
        syncScales: queryResult.cardSettings.syncScales,
        selectedXAxis: queryResult.cardSettings.selectedXAxis,
        selectedYAxis: queryResult.cardSettings.selectedYAxis,
        sortColumn: queryResult.cardSettings.sortColumn,
        sortOrder: queryResult.cardSettings.sortOrder,
      };
    }
    
    let focusType = 'none';
    if (context.mentions && context.mentions.length > 0) {
      focusType = 'mention';
    } else if (context.isQuickFilter) {
      focusType = 'quickFilter';
    } else if (context.isTangoId) {
      focusType = 'tangoId';
    }

    // Create a clean context object without duplicate valueContext
    const cleanContext = {
      ...context,
      columnConfig,
      isIndexed: context.isFromValueSearchList || context.isIndexed || false,
      valueContext: context.valueContext || null,
      usesTableMention: context.usesTableMention || false
    };


    // NEW CODE: Check for admin/AI Manager direct execution
    if (context.isDirectExecution && context.directExecutionId) {
      // Admin or AI Manager check
      if (!isAdmin && !isAIManager) {
        setError({
          error: 'Admin or AI Manager access required',
          details: { message: 'Only administrators and AI managers can directly execute queries by document ID' }
        });
        return;
      }

      try {
        onQueryStart(); // Start loading state
        
        
        // Use the runRecentSearch endpoint directly instead of the normal query processing
        const endpoint = 'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/runRecentSearch';
        const body = {
          data: {
            documentId: context.directExecutionId,
            userId: currentUser.email,
            isAdminExecution: true // Optional: flag for tracking admin executions
          },
        };
        
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }
        
        const responseData = await response.json();
        
        if (responseData.error) {
          throw new Error(responseData.error);
        }
        
        // Handle truncation message consistently for direct execution
        if (responseData.truncationMessage) {
          setTruncationMessage(responseData.truncationMessage);
        } else if (responseData.result && responseData.result.length > 2000) {
          setTruncationMessage('The query result has been limited to 2,000 rows. Metrics are calculated on displayed rows only.');
        }
        
        // Enhanced response data with admin execution info
        const enhancedResponse = {
          ...responseData,
          isAdminExecution: true,
          displayQuery: `Direct execution: ${context.directExecutionId}`,
          userQuery: context.displayQuery || `run_${context.directExecutionId}`,
          adminExecutor: currentUser.email,
          executionTime: new Date().toISOString()
        };
        
        // Process the response like a normal query result
        onQueryResult(enhancedResponse);
        setShowResultCard(true);
        
        // Scroll to results
        setTimeout(() => {
          if (mainContentRef && mainContentRef.current) {
            mainContentRef.current.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }
        }, 100);
        
        return; // Exit early after handling
      } catch (error) {
        setError({
          error: error.message || 'Failed to execute query',
          details: { documentId: context.directExecutionId }
        });
        onQueryResult(null);
        return;
      }
    }

    // Original tango_ID handling
    if (context.isTangoId && context.tangoDocumentId) {
      try {
        onQueryStart(); // Start loading state
        
        // Fetch the document from Firestore
        const db = getFirestore();
        const docRef = doc(db, 'LLM Queries', context.tangoDocumentId);
        const docSnapshot = await getDoc(docRef);
        
        if (docSnapshot.exists()) {
          const docData = docSnapshot.data();
          
          if (docData && (docData.code || docData.sqlQuery)) {
            
            // Use the SQL code but keep the original tango-ID for display
            handleSubmit(
              docData.code || docData.sqlQuery, // SQL query to execute - check both fields
              false,
              '',
              '',
              false,
              null,
              [],
              'tangoId',
              {
                ...cleanContext,
                displayQuery: queryOrResult, // Original tango-ID input
                originalQuestion: docData.userQuestion || queryOrResult,
                tangoDocumentId: context.tangoDocumentId
              }
            );
            return; // Exit early after handling
          } else {
            setError({
              error: 'The specified query document does not contain SQL code',
              details: { documentId: context.tangoDocumentId }
            });
          }
        } else {
          setError({
            error: 'Query document not found',
            details: { documentId: context.tangoDocumentId }
          });
        }
      } catch (error) {
        setError({
          error: `Error fetching document: ${error.message}`,
          details: { documentId: context.tangoDocumentId }
        });
      }
    }

    // Standard query processing (will run if tango-ID handling fails)
    if (typeof queryOrResult === 'object' && queryOrResult.documentId) {
      // Recent search
      handleSubmit(
        queryOrResult.userQuery, 
        false, 
        '', 
        '', 
        true, 
        queryOrResult.documentId, 
        [], 
        focusType, 
        cleanContext
      );
    } else if (isFollowUpMode) {
      handleFollowUpQuery(
        queryOrResult, 
        selectedText, 
        queryResult?.sqlQuery, 
        [], 
        {
          ...cleanContext,
          valueContext: cleanContext.valueContext,
          usesTableMention: cleanContext.usesTableMention
        }
      );
    } else {
      // Check if this is from QuickFilter and has a display text
      const actualQuery = context.isQuickFilter && context.displayText 
        ? context.displayText 
        : queryOrResult;
      
      handleSubmit(
        actualQuery, 
        false, 
        selectedText, 
        '', 
        false, 
        null, 
        [], 
        focusType, 
        {
          ...cleanContext,
          originalQuery: context.isQuickFilter ? queryOrResult : null
        }
      );
    }
  },
  [handleFollowUpQuery, handleSubmit, queryResult, onQueryStart, setError, isAdmin, isAIManager, currentUser, config, mainContentRef]
);
  
      async function handleSaveQuery(cardTitle) {
        if (!queryResult) return;
        
        // Use the provided title or fall back to the user query
        const autoTitle = cardTitle || queryResult.userQuery || 'Untitled Query';
        setQueryName(autoTitle);
        
        // Skip the modal and save directly
        handleSaveQuerySubmit(autoTitle);
      }
  
      const handleSaveQuerySubmit = async (providedTitle = null) => {
        const titleToUse = providedTitle || queryName;
        
        if (!titleToUse || isSaving) return;
      
        setIsSaving(true);
        try {
          const response = await fetch(
            'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/saveQuery',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                data: {
                  userId: currentUser.email,
                  name: titleToUse,
                  userQuery: queryResult.userQuery,
                  sqlQuery: queryResult.sqlQuery,
                  llmModel: queryResult.config.usedModel,
                  fallbackUsed: queryResult.config.fallbackUsed,
                  originalModel: queryResult.originalModel,
                  documentId: queryResult.documentId,
                  isPrivate: !isShared,
                  explanation: explanation,
                  schema: queryResult.schema,
                },
              }),
            }
          );
          const { id } = await response.json();
          setSavedQueryId(id);
          
          // Don't open the save modal, go directly to add to dashboard modal
          setIsSaveQueryModalOpen(false);
          setIsQueryJustAdded(false);
          setIsAddToDashboardModalOpen(true);
          
          if (onSaveQuery) {
            onSaveQuery();
          }
          // Refresh recent searches after saving a query
          fetchRecentSearches();
        } catch (error) {
          setError('Failed to save query. Please try again.');
        } finally {
          setIsSaving(false);
        }
      };
  
      const handleAddToDashboard = async (dashboardId) => {
        try {
          const response = await fetch(
            'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/handleDashboardOperation',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                data: {
                  operation: 'addQueryToDashboard',
                  reportData: {
                    dashboardId: dashboardId,
                    query: {
                      id: savedQueryId,
                      name: queryName,
                      userQuery: queryResult.userQuery,
                      sqlQuery: queryResult.sqlQuery,
                      result: queryResult.result,
                    },
                  },
                  userId: currentUser.email,
                },
              }),
            }
          );
          if (!response.ok) {
            throw new Error('Failed to add query to dashboard.');
          }
          const updatedDashboard = await response.json();
          setIsQueryJustAdded(true);
          if (onDashboardUpdate) {
            onDashboardUpdate(updatedDashboard);
          }
          if (!isAskPage && dashboardManager && typeof dashboardManager.handleSelectDashboard === 'function') {
            await dashboardManager.handleSelectDashboard(updatedDashboard);
          } else if (isAskPage) {
          }
          setIsAddToDashboardModalOpen(false);
        } catch (error) {
          setError('Failed to add query to dashboard. Please try again.');
        }
      };
  
      const handleCreateNewDashboard = async (dashboardName) => {
        try {
          const response = await fetch(
            'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/handleDashboardOperation',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                data: {
                  operation: 'create',
                  reportData: {
                    name: dashboardName,
                    queries: [savedQueryId],
                  },
                  userId: currentUser.email,
                },
              }),
            }
          );
          if (!response.ok) {
            throw new Error('Failed to create new dashboard.');
          }
          const newDashboard = await response.json();
          if (!newDashboard || !newDashboard.id) {
            throw new Error('Invalid dashboard data received from server.');
          }
          setIsQueryJustAdded(true);
          if (onDashboardUpdate) {
            onDashboardUpdate(newDashboard);
          }
          setIsAddToDashboardModalOpen(false);
          return newDashboard;
        } catch (error) {
          setError('Failed to create new dashboard. Please try again.');
          return null;
        }
      };
  
      const handleExplanationClick = async () => {
        if (explanation) return;
        try {
          const newExplanation = await generateExplanation(
            queryResult.sqlQuery,
            queryResult.documentId,
            queryResult.savedQueryId
          );
          setExplanation(newExplanation);
          onQueryResult({
            ...queryResult,
            explanation: newExplanation,
          });
        } catch (error) {
          setError('Failed to generate explanation. Please try again.');
        }
      };

      const hasSavedQueries = useMemo(() => {
        return getFilteredQueries && getFilteredQueries('').length > 0;
      }, [getFilteredQueries]);
  
      useEffect(() => {
        if (queryResult && queryResult.result) {
          setShowResultCard(true);
          setExplanation(queryResult.explanation || '');
          setTruncationMessage(queryResult.truncationMessage || '');
        }
      }, [queryResult]);
  
      return (
        <ErrorBoundary>
          <div ref={componentRef}>
            <QueryInput
              onSubmit={handleQueryInputSubmit}
              isLoading={isLoading || isConfigLoading}
              isFollowUp={isFollowUp}
              fetchRecentSearches={fetchRecentSearches}
              initialRecentSearches={recentSearches}
              selectedText={selectedText}
              isFollowUpMode={isFollowUp}
              onTextSelection={handleTextSelection}
              initialQuery={currentQuery}
              queryResult={queryResult}
              hideProposedQuestions={hideProposedQuestions || (showResultCard && !!queryResult) || hasSavedQueries}
              />
            {error && <QueryError error={error} onClose={() => setError(null)} />}
            {queryResult && showResultCard && (
              <div ref={resultRef}>
                <QueryResultCard
                  ref={actionPanelRef}
                  query={queryResult}
                  currentUser={currentUser}
                  onSaveQuery={handleSaveQuery}
                  onAddToLibrary={onAddToLibrary}
                  onAddToDashboard={() => setIsAddToDashboardModalOpen(true)}
                  onExplanationClick={handleExplanationClick}
                  isExplanationLoading={isExplanationLoading}
                  isExplanationPending={isExplanationPending}
                  truncationMessage={truncationMessage}
                  onFollowUpQuery={handleFollowUpQuery}
                  isDashboardMode={isDashboardMode}
                  fetchRecentSearches={fetchRecentSearches}
                  onTextSelection={handleTextSelection}
                  isAdmin={isAdmin}
                  isAIManager={isAIManager}
                  isLoading={isLoading || isConfigLoading}
                  onClose={handleClose}
                  onQueryResult={onQueryResult}
                  saveQuerySettings={saveQuerySettings}
                  getQuerySettings={getQuerySettings}
                  // Intentionally not displaying intentLog in the UI unless needed
                  // If we want to display it, we would pass it here
                  // intentLog={queryResult.intentLog}
                />
              </div>
            )}
            {/* SaveQueryModal is no longer needed since we use automatic titles
              <SaveQueryModal
                isOpen={isSaveQueryModalOpen}
                onClose={() => setIsSaveQueryModalOpen(false)}
                onSubmit={handleSaveQuerySubmit}
                queryName={queryName}
                setQueryName={setQueryName}
                isShared={isShared}
                setIsShared={setIsShared}
                isSaving={isSaving}
              />
              */}
            <AddToDashboardModal
              isOpen={isAddToDashboardModalOpen}
              onClose={() => {
                setIsAddToDashboardModalOpen(false);
                setIsQueryJustAdded(false);
              }}
              dashboards={dashboards}
              onAddToDashboard={handleAddToDashboard}
              onCreateNewDashboard={handleCreateNewDashboard}
              onDashboardUpdate={onDashboardUpdate}
              isQueryJustAdded={isQueryJustAdded}
              fetchDashboards={fetchDashboards}
            />
          </div>
        </ErrorBoundary>
      );
    }
  );
  
  export default QueryComponent;